import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { tooltipManager } from "../utils/tooltipManager";
import "./Tooltip.css";

const INITIAL_POSITION = Object.freeze({ top: 0, left: 0, arrowX: 24, placement: "bottom", ready: false });

function clamp(value, min, max) {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

function getViewportRect() {
  const viewport = window.visualViewport;
  const left = viewport?.offsetLeft || 0;
  const top = viewport?.offsetTop || 0;
  const width = viewport?.width || window.innerWidth || document.documentElement.clientWidth;
  const height = viewport?.height || window.innerHeight || document.documentElement.clientHeight;
  return { left, top, right: left + width, bottom: top + height, width, height };
}

export default function Tooltip({ id, text, children, placement = "bottom", priority = 0, showDelay = 1200, enabled = true, block = false, allowDuringTour = false, sequence = "", order = 0, maxWidth = 320, viewportMargin = null }) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState(INITIAL_POSITION);
  const anchorRef = useRef(null);
  const bubbleRef = useRef(null);

  useEffect(() => tooltipManager.subscribe((activeId) => setVisible(activeId === id)), [id]);

  useEffect(() => () => tooltipManager.release(id), [id]);

  useEffect(() => {
    if (!visible) setPosition(INITIAL_POSITION);
  }, [visible]);

  const dismiss = useCallback(() => {
    tooltipManager.markTooltipAsSeen(id);
    tooltipManager.dismiss(id);
  }, [id]);

  useEffect(() => {
    if (!id || !enabled) return undefined;

    let timer = 0;
    let unsubscribeReady = () => {};

    const schedule = () => {
      if (tooltipManager.hasSeenTooltip(id)) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        tooltipManager.requestShow(id, priority, { allowDuringTour, sequence, order });
      }, showDelay);
    };

    if (tooltipManager.isReady()) schedule();
    else unsubscribeReady = tooltipManager.onAuthReady(schedule);

    return () => {
      window.clearTimeout(timer);
      unsubscribeReady();
    };
  }, [id, enabled, priority, showDelay, allowDuringTour, sequence, order]);

useEffect(() => {
  if (enabled) return;
  tooltipManager.release(id);
}, [enabled, id]);

  useEffect(() => {
    if (!visible) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") dismiss();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [visible, dismiss]);

  useLayoutEffect(() => {
    if (!visible) return undefined;

    let frame = 0;
    let resizeObserver;

    const measure = () => {
      frame = 0;

      const anchor = anchorRef.current;
      const bubble = bubbleRef.current;
      if (!anchor || !bubble || !anchor.isConnected || !bubble.isConnected) return;

      const viewport = getViewportRect();
      const edgeMargin = Number.isFinite(viewportMargin) ? viewportMargin : viewport.width >= 768 ? 24 : 16;
      const gap = 12;
      const anchorRect = anchor.getBoundingClientRect();
      const bubbleRect = bubble.getBoundingClientRect();
      const preferredPlacement = placement === "top" ? "top" : "bottom";
      const bottomTop = anchorRect.bottom + gap;
      const topTop = anchorRect.top - gap - bubbleRect.height;
      const bottomFits = bottomTop + bubbleRect.height <= viewport.bottom - edgeMargin;
      const topFits = topTop >= viewport.top + edgeMargin;
      let resolvedPlacement = preferredPlacement;

      if (preferredPlacement === "bottom" && !bottomFits && topFits) resolvedPlacement = "top";
      if (preferredPlacement === "top" && !topFits && bottomFits) resolvedPlacement = "bottom";
      if (!bottomFits && !topFits) {
        const spaceBelow = viewport.bottom - anchorRect.bottom;
        const spaceAbove = anchorRect.top - viewport.top;
        resolvedPlacement = spaceBelow >= spaceAbove ? "bottom" : "top";
      }

      const idealTop = resolvedPlacement === "bottom" ? bottomTop : topTop;
      const top = clamp(idealTop, viewport.top + edgeMargin, viewport.bottom - edgeMargin - bubbleRect.height);
      const anchorCenter = anchorRect.left + anchorRect.width / 2;
      const idealLeft = anchorCenter - bubbleRect.width / 2;
      const left = clamp(idealLeft, viewport.left + edgeMargin, viewport.right - edgeMargin - bubbleRect.width);
      const arrowX = clamp(anchorCenter - left, 20, bubbleRect.width - 20);

      setPosition({ top, left, arrowX, placement: resolvedPlacement, ready: true });
    };

    const scheduleMeasure = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };

    measure();
    const secondFrame = window.requestAnimationFrame(scheduleMeasure);
    const delayedMeasure = window.setTimeout(scheduleMeasure, 60);

    window.addEventListener("resize", scheduleMeasure);
    window.addEventListener("orientationchange", scheduleMeasure);
    window.addEventListener("scroll", scheduleMeasure, true);
    window.visualViewport?.addEventListener("resize", scheduleMeasure);
    window.visualViewport?.addEventListener("scroll", scheduleMeasure);

    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(scheduleMeasure);
      if (anchorRef.current) resizeObserver.observe(anchorRef.current);
      if (bubbleRef.current) resizeObserver.observe(bubbleRef.current);
    }

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(secondFrame);
      window.clearTimeout(delayedMeasure);
      window.removeEventListener("resize", scheduleMeasure);
      window.removeEventListener("orientationchange", scheduleMeasure);
      window.removeEventListener("scroll", scheduleMeasure, true);
      window.visualViewport?.removeEventListener("resize", scheduleMeasure);
      window.visualViewport?.removeEventListener("scroll", scheduleMeasure);
      resizeObserver?.disconnect();
    };
  }, [visible, placement, text, viewportMargin]);

  const Anchor = block ? "div" : "span";
  const portalTarget = typeof document !== "undefined" ? document.body : null;

  return (
    <>
      <Anchor ref={anchorRef} className={`tooltip-anchor ${block ? "tooltip-anchor--block" : "tooltip-anchor--inline"}`} data-tooltip-id={id} onClick={visible ? dismiss : undefined}>
        {children}
      </Anchor>

      {visible && portalTarget && createPortal(
        <div ref={bubbleRef} id={`tooltip-${id}`} className={`tooltip-popover tooltip-popover--${position.placement} ${position.ready ? "is-ready" : ""}`} role="tooltip" style={{ top: `${position.top}px`, left: `${position.left}px`, "--tooltip-arrow-x": `${position.arrowX}px`, "--tooltip-max-width": `${maxWidth}px`, ...(Number.isFinite(viewportMargin) ? { "--tooltip-edge-space": `${viewportMargin * 2}px` } : {}) }} onClick={dismiss}>
          <span className="tooltip-popover__arrow" aria-hidden="true" />
          <span className="tooltip-popover__text">{text}</span>
        </div>,
        portalTarget,
      )}
    </>
  );
}
